import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { EnumSection, IAvailableCompetition, ISelectedEvolutions } from '../../interfaces/clientInterfaces';
import { INumberDictionary, IStore } from '../../interfaces';
import { IDBCompetition } from '../../interfaces/dbInterfaces';
import CompetitionEvolution from './CompetitionsEvolutionContainer';
import * as actions from '../../actions';
import { Competitions as CompetitionsUtils } from '../../utils/competitions';
import Editions from './Editions';
import Countries from './Countries';
import Participations from './Participations';
import Finalists from './Finalists';
import { useAppDispatch } from '../../utils/modules';
import { ISelectItem, Select } from './Select/Select';
import styles from './Competitions.module.css';

interface ICompetitionContentInjectedProps {
	competitionsWithEvolution: INumberDictionary<IAvailableCompetition>;
	isMobileViewActive: boolean;
	competitionsDictionary: INumberDictionary<IDBCompetition>;
	selectedEvolutions: ISelectedEvolutions[];
	availableCompetitions: IAvailableCompetition[]
}

enum CompetitionSectionEnum {
	evolution = 'evolution',
	participantsMap = 'participantsMap',
	editions = 'editions',
	countries = 'countries',
	participations = 'participations',
	finalists = 'finalists'
}

const EVOLUTION_KEY = 'Performance by club'
const EDITIONS_KEY = 'List of editions'
const WINNERS_BY_COUNTRY = 'Winners by country'
const NUMBER_OF_PARTICIPATIONS = 'Number of participations';
const MAP_OF_WINNERS = 'Map of winners';

export default function CompetitionContent() {

	const dispatch = useAppDispatch();

	const {
		competitionsWithEvolution,
		availableCompetitions,
		isMobileViewActive,
		competitionsDictionary,
		selectedEvolutions,
	} = useSelector(
		(completeState: IStore): ICompetitionContentInjectedProps => {
			return {
				competitionsWithEvolution: completeState.competitions.competitionsWithEvolution,
				isMobileViewActive: completeState.ui.isMobileViewActive,
				competitionsDictionary: completeState.competitions.competitionsDictionary,
				selectedEvolutions: completeState.competitions.selectedEvolutions,
				availableCompetitions: completeState.competitions.availableCompetitions
			}
		});
	
	const [section, setSection] = useState(CompetitionSectionEnum.evolution);

	useEffect(() => {
		dispatch(actions.setSectionName(EnumSection.competitions));
		const params = new URLSearchParams(window.location.search);
		const competitionParameter = params.get('competition');
		const competition = availableCompetitions.find(comp => comp.name === competitionParameter);
		if (competition) {
			const competitionToSelect = competitionsWithEvolution[competition.id];
			if (competitionToSelect) {
				dispatch(actions.addCompetition(competitionToSelect.id));
			}
		}
		
	}, [dispatch, availableCompetitions, competitionsWithEvolution]);

	const competition = useMemo(
		(): IDBCompetition | null => {
			const selectedEvolution = selectedEvolutions.length > 0 ? selectedEvolutions[0] : null;
			if (!selectedEvolution) {
				return null;
			}
			const availableCompetition = competitionsWithEvolution[selectedEvolution.competitionId]
			if (!availableCompetition) {
				return null;
			}
			const competition = competitionsDictionary[availableCompetition?.id];
			if (!competition) {
				return null;
			}
			return competition;
		},
		[competitionsWithEvolution, competitionsDictionary, selectedEvolutions]
	);

	const canRankByCountry = useMemo(
		(): boolean => {
			return competition?.isContinental ||
				competition?.isInternational ||
				competition?.region_code === 'YU' ||
				competition?.region_code === 'UR' ||
				competition?.region_code === 'ZK' || 
				(competition?.region_code === 'WL' && competition?.competition_code === 'C')
		},
		[competition]
	);

	const hasParticipations = useMemo(
		(): boolean => {
			return !!(competition?.totalEditions && competition?.totalEditions > 0);
		},
		[competition?.totalEditions]
	);

	const screenItems = useMemo(
		(): ISelectItem[] => {
			const screenItems: ISelectItem[] = [
				{
					id: EVOLUTION_KEY,
					text: EVOLUTION_KEY
				},
				{
					id: EDITIONS_KEY,
					text: EDITIONS_KEY
				},
				
			];
			if (canRankByCountry) {
				screenItems.push({
					id: WINNERS_BY_COUNTRY,
					text: WINNERS_BY_COUNTRY
				});
			}
			if (hasParticipations) {
				screenItems.push({
					id: NUMBER_OF_PARTICIPATIONS,
					text: NUMBER_OF_PARTICIPATIONS
				});
			}
			screenItems.push({
				id: MAP_OF_WINNERS,
				text: MAP_OF_WINNERS
			});
			return screenItems;
		},
		[canRankByCountry, hasParticipations]
	);

	useEffect(() => {
		if (section === CompetitionSectionEnum.countries) {
			if (!canRankByCountry) {
				setSection(CompetitionSectionEnum.evolution);
			}
		} else if (section === CompetitionSectionEnum.participations) {
			if (!hasParticipations) {
				setSection(CompetitionSectionEnum.evolution);
			}
		}
	}, [competition, section, canRankByCountry, hasParticipations]);

	const competitionDropDownOptions = useMemo(
		(): ISelectItem[] => {
			const competitionDropDownOptions = availableCompetitions.map(aC => {
				return {
					text : aC.name, 
					id: aC.id.toString(),
					icon: [CompetitionsUtils.getCompetitionRegionImagePath(aC.regionCode), CompetitionsUtils.getRegionalTournamentImagePath(aC)]
				};
			});
			return competitionDropDownOptions;
		},
		[availableCompetitions]
	);

	const onAddCompetition = useCallback((value: string | null) => {
		if (value) {
			const competitionId = parseInt(value);
			if (!Number.isNaN(competitionId)) {
				dispatch(actions.addCompetition(competitionId));
			}
		}
	}, [dispatch]);

	const selectedScreenItem = useMemo(
		(): string => {
			switch (section) {
				case CompetitionSectionEnum.countries:
					return WINNERS_BY_COUNTRY;
				case CompetitionSectionEnum.participations:
					return NUMBER_OF_PARTICIPATIONS;
				case CompetitionSectionEnum.evolution:
					return EVOLUTION_KEY;
				case CompetitionSectionEnum.editions:
					return EDITIONS_KEY;
				case CompetitionSectionEnum.finalists:
				default:
					return MAP_OF_WINNERS;
			}
		},
		[section]
	);

	const onSelectScreen = useCallback((value: string | null): void => {
		switch (value) {
			case EVOLUTION_KEY: {
				setSection(CompetitionSectionEnum.evolution);
				break;
			}
			case EDITIONS_KEY: {
				setSection(CompetitionSectionEnum.editions);
				break;
			}
			case WINNERS_BY_COUNTRY: {
				setSection(CompetitionSectionEnum.countries);
				break;
			}
			case NUMBER_OF_PARTICIPATIONS: {
				setSection(CompetitionSectionEnum.participations);
				break;
			}
			case MAP_OF_WINNERS:
			default: {
				setSection(CompetitionSectionEnum.finalists);
				break;
			}
		}
	}, []);

	const renderSection = () => {
		switch (section) {
			case CompetitionSectionEnum.evolution: {
				return <CompetitionEvolution/>;
			}
			case CompetitionSectionEnum.editions: {
				return <Editions/>;
			}
			case CompetitionSectionEnum.countries: {
				return <Countries/>;
			}
			case CompetitionSectionEnum.participations: {
				return <Participations />;
			}
			case CompetitionSectionEnum.finalists: {
				return <Finalists />;
			}
			default:
				return null;
		}
	}

	

	return (
		<div
			className={styles.competitionsContainer}
			style={{
				background: 'url(\'../img/background/competition.jpg\') no-repeat center center fixed' 
			}}
		>
			<div
				className={isMobileViewActive ? styles.mobileCompetitionsHeader : styles.competitionsHeader}
			>
				<Select
					placeholder={'Select a competition from the list'}
					selectedKey={competition?.competition_id.toString() || ''}
					items={competitionDropDownOptions}
					onSelectionChange={onAddCompetition}
					searchable={true}
					popoverFixSize={true}
				/>
				<Select
					selectedKey={selectedScreenItem}
					items={screenItems}
					onSelectionChange={onSelectScreen}
				/>
			</div>
			
			{
				competition && !!competition.description &&
					<div
						className={styles.competitionDescriptionContainer}
						style={{
							maxHeight: isMobileViewActive ? '80px' : '100px'
						}}
					>
						{competition.description}
					</div>
			}
			{renderSection()}
		</div>
	);
}
